import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Table, TableProps } from 'antd';
import { IGetApiType } from '../../REST';
import { useUiIsSpinnerFound } from '../../hooks';
import { Catcher } from '../Catcher';

export type ILoadMoreResult = {
    totalCount: number;
};

export type IInfiniteTableProps<IDataType> = Omit<TableProps<IDataType>, 'pagination'> & {
    columns: TableProps<IDataType>['columns'];
    dataSource: IDataType[];
    idSpinners?: IGetApiType[];
    isLoading?: boolean;
    loadMore?: (pageNumber: number) => Promise<ILoadMoreResult>;
    resetKey?: string;
    rowKey?: never;
    scrollY?: number;
    threshold?: number;
};

// !Warning! PageSize need to be more than scrollY

export function InfiniteTable<IDataType extends object>({
    columns,
    dataSource,
    idSpinners,
    isLoading = false,
    loadMore,
    resetKey,
    scrollY = 600,
    threshold = 100,
    ...tableProps
}: IInfiniteTableProps<IDataType>) {
    const wrapperRef = useRef<HTMLDivElement>(null);

    const [currentPage, setCurrentPage] = useState(1);
    const [totalCount, setTotalCount] = useState(0);
    const [isLocalLoading, setILocalLoading] = useState(false);
    const isSpinnersLoading = useUiIsSpinnerFound(idSpinners);
    const isLoadingFinal = isLoading || isSpinnersLoading || isLocalLoading;

    const hasMore = totalCount === 0 || dataSource.length < totalCount;

    useEffect(() => {
        setCurrentPage(1);
        setTotalCount(0);
    }, [resetKey]);

    const loadNextPage = useCallback(async () => {
        if (!loadMore || isLoadingFinal || !hasMore) {
            return;
        }
        const nextPage = currentPage + 1;
        setILocalLoading(true);
        const result = await loadMore(nextPage);
        setILocalLoading(false);
        setCurrentPage(nextPage);
        setTotalCount(result.totalCount);
    }, [currentPage, hasMore, isLoadingFinal, loadMore]);

    const handleScroll = useCallback(
        (event: Event) => {
            const target = event.target as HTMLDivElement;

            const isNearBottom = target.scrollTop + target.clientHeight >= target.scrollHeight - threshold;
            if (isNearBottom) {
                void loadNextPage();
            }
        },
        [loadNextPage, threshold],
    );

    useEffect(() => {
        const wrapper = wrapperRef.current;
        if (!wrapper) {
            return;
        }
        const tableBody = wrapper.querySelector('.ant-table-body') as HTMLDivElement | null;
        if (!tableBody) {
            return;
        }
        tableBody.addEventListener('scroll', handleScroll);
        return () => {
            tableBody.removeEventListener('scroll', handleScroll);
        };
    }, [handleScroll]);

    return (
        <Catcher>
            <div ref={wrapperRef}>
                <Table<IDataType>
                    {...tableProps}
                    className={'table-theme'}
                    loading={isLoadingFinal}
                    columns={columns}
                    dataSource={dataSource}
                    pagination={false}
                    scroll={{
                        y: scrollY,
                        ...(tableProps.scroll ?? {}),
                    }}
                />
            </div>
        </Catcher>
    );
}
